import { useMemo, useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import { routes } from 'config/routes'
import {
	ActionsSelect,
	BookingEngineTabs,
	CardHeader,
	CardWrapper,
	ConfirmModal,
	CtaLink,
	DeleteConfirmModal,
	EmptyTable,
	Header,
	SearchInput
} from 'shared/components'
import { useDevice, useModal, useTableSelect } from 'shared/hooks'
import { matchesSearch } from 'utils/matchesSearch'
import { useActionFilters } from './hooks/useActionFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useDeleteRentalMutation } from './mutations/useDeleteRentalMutation'
import { useDeleteBulkRentalMutation } from './mutations/useDeleteBulkRentalMutation'
import { useSyncRentalsToReglaMutation } from './mutations/useSyncRentalsToReglaMutation'
import { useFetchRentals } from './queries/useFetchRentals'
import { TableSection } from './organisms/TableSection'

const RentalsPage = () => {
	const { t } = useTranslation()
	const { isMobileOrTablet } = useDevice()
	const [search, setSearch] = useState('')
	const [checkedRentals, setCheckedRentals] = useState<number[]>([])
	const [idToBeRemoved, setIdToBeRemoved] = useState<number | null>(null)
	const { rentals, loadingRentals } = useFetchRentals()
	const results = useMemo(
		() =>
			(rentals?.data?.results ?? []).filter((rental) =>
				matchesSearch(search, [rental.title, rental.booth_type_name?.name])
			),
		[rentals, search]
	)
	const { select } = useTableSelect({ nodes: results }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const clearSelection = () => select.fns.onRemoveAll()
	const [actionsFilter] = useActionFilters(!checkedRentals.length)

	const {
		isOpen: isDeleteModalOpen,
		openModal: openDeleteModal,
		closeModal: closeDeleteModal
	} = useModal()
	const {
		isOpen: isBulkDeleteModalOpen,
		openModal: openBulkDeleteModal,
		closeModal: closeBulkDeleteModal
	} = useModal()
	const {
		isOpen: isReglaModalOpen,
		openModal: openReglaModal,
		closeModal: closeReglaModal
	} = useModal()

	const { rentalDeleteMutation } = useDeleteRentalMutation(closeDeleteModal)

	const { bulkDeleteMutation } = useDeleteBulkRentalMutation({
		onSettled: () => {
			clearSelection()
			closeBulkDeleteModal()
		}
	})

	const { syncRentalsToReglaMutation } = useSyncRentalsToReglaMutation(() => {
		clearSelection()
		closeReglaModal()
	})

	function onSelectChange(_action: any, state: any) {
		setCheckedRentals(state.ids)
	}

	const handleDelete = (id: number) => {
		setIdToBeRemoved(id)
		openDeleteModal()
	}

	const menuOptions = useContextMenuOptions({ onDelete: handleDelete })

	const handleAction = (action: string) => {
		switch (action) {
			case 'delete':
				openBulkDeleteModal()
				break
			case 'regla':
				openReglaModal()
				break
		}
	}

	const renderContent = () => {
		if (loadingRentals) return <EmptyTable isLoading />
		if (!rentals || !results.length)
			return <EmptyTable text={search ? t('No results') : t('No rentals yet')} />

		return (
			<TableSection rentals={rentals} results={results} select={select} menuOptions={menuOptions} />
		)
	}

	return (
		<>
			<Header
				title={t('Booking engine')}
				tabs={<BookingEngineTabs />}
				actions={
					!isMobileOrTablet && (
						<ActionsSelect options={actionsFilter.options} onChange={handleAction} />
					)
				}
			/>
			<div className="px-5 lg:flex lg:max-h-[calc(100vh-113.5px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<CardHeader
						title={t('Rental')}
						description={t('Rental pricing and periods for bookings.')}>
						<SearchInput
							variant="filled"
							className="sm:w-64"
							label={t('Search rentals')}
							placeholder={t('Search rentals')}
							onChange={(event) => setSearch(event.target.value)}
						/>
						<CtaLink to={routes.rentalAdd} text={t('Add rental')} />
					</CardHeader>
					{renderContent()}
				</CardWrapper>
			</div>

			{isDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete rental')}
					isOpened={isDeleteModalOpen}
					closeModal={closeDeleteModal}
					confirm={() => rentalDeleteMutation(idToBeRemoved as number)}>
					<p>{t('You are about to remove a rental. Are you sure you want to proceed?')}</p>
				</DeleteConfirmModal>
			)}
			{isBulkDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete selected rentals')}
					isOpened={isBulkDeleteModalOpen}
					closeModal={closeBulkDeleteModal}
					confirm={() => bulkDeleteMutation(checkedRentals)}>
					<p>
						{t('You are about to delete {{ count }} rentals. Are you sure you want to proceed?', {
							count: checkedRentals.length
						})}
					</p>
				</DeleteConfirmModal>
			)}
			{isReglaModalOpen && (
				<ConfirmModal
					title={t('Sync with Regla')}
					isOpened={isReglaModalOpen}
					closeModal={closeReglaModal}
					confirm={() => syncRentalsToReglaMutation(checkedRentals)}>
					<p>
						{t(
							'You are about to sync {{ count }} rentals with Regla. Are you sure you want to proceed?',
							{ count: checkedRentals.length }
						)}
					</p>
				</ConfirmModal>
			)}
		</>
	)
}

export default RentalsPage
